import pluralize from "pluralize";

// util
import { ZonedMoment } from "@util/time-zone";
import { validateEventTime, validateEventMoved } from "@util/event-time";
import { CalendarStatus, DefragExplainedMove, Event, Maybe, MoonPhase } from "@clockwise/schema";
import { SetRequired } from "@types";

/**
 * Relative time.
 */
const MILLIS_IN = {
  ONE_SECOND: 1000,
  ONE_MINUTE: 60000,
  ONE_HOUR: 3600000,
  ONE_DAY: 86400000,
  ONE_WEEK: 604800000,
  ONE_MONTH: 2628000000,
  ONE_YEAR: 31540000000,
};

function formatRelativeTime(time: number, unit: string, isPast: boolean, abbreviate?: boolean) {
  const value = abbreviate ? `${time}${unit}` : pluralize(unit, time, true);
  return `${value} ${isPast ? "from now" : "ago"}`;
}

export function getRelativeTimeMillis(time: number, abbreviate?: boolean) {
  return getRelativeTime(new ZonedMoment(time), abbreviate);
}

export function getRelativeTime(time: ZonedMoment, abbreviate?: boolean) {
  const now = new ZonedMoment();
  // + => future, - => past
  const diffInMillis = time.diff(now);

  if (Math.abs(diffInMillis) < MILLIS_IN.ONE_SECOND) {
    // less than one second
    return "just now";
  } else if (Math.abs(diffInMillis) < MILLIS_IN.ONE_MINUTE) {
    // less than one minute
    const seconds = Math.round(Math.abs(diffInMillis) / MILLIS_IN.ONE_SECOND);
    return formatRelativeTime(
      seconds,
      `${abbreviate ? "s" : "second"}`,
      diffInMillis > 0,
      abbreviate,
    );
  } else if (Math.abs(diffInMillis) < MILLIS_IN.ONE_HOUR) {
    // less than one hour
    const minutes = Math.round(Math.abs(diffInMillis) / MILLIS_IN.ONE_MINUTE);
    return formatRelativeTime(
      minutes,
      `${abbreviate ? "min" : "minute"}`,
      diffInMillis > 0,
      abbreviate,
    );
  } else if (Math.abs(diffInMillis) < MILLIS_IN.ONE_DAY) {
    // less than one day
    const hours = Math.round(Math.abs(diffInMillis) / MILLIS_IN.ONE_HOUR);
    return formatRelativeTime(hours, `${abbreviate ? "hr" : "hour"}`, diffInMillis > 0, abbreviate);
  } else if (Math.abs(diffInMillis) < MILLIS_IN.ONE_WEEK) {
    // less than one week
    const days = Math.round(Math.abs(diffInMillis) / MILLIS_IN.ONE_DAY);
    if (days === 1) {
      return `${diffInMillis > 0 ? "tomorrow" : "yesterday"}`;
    }
    return formatRelativeTime(days, `${abbreviate ? "d" : "day"}`, diffInMillis > 0, abbreviate);
  } else if (Math.abs(diffInMillis) < MILLIS_IN.ONE_MONTH) {
    // less than one month
    const weeks = Math.round(Math.abs(diffInMillis) / MILLIS_IN.ONE_WEEK);
    return formatRelativeTime(weeks, `${abbreviate ? "wk" : "week"}`, diffInMillis > 0, abbreviate);
  } else if (Math.abs(diffInMillis) < MILLIS_IN.ONE_YEAR) {
    // less than one year
    const months = Math.round(Math.abs(diffInMillis) / MILLIS_IN.ONE_MONTH);
    return formatRelativeTime(
      months,
      `${abbreviate ? "mo" : "month"}`,
      diffInMillis > 0,
      abbreviate,
    );
  } else {
    const years = Math.round(Math.abs(diffInMillis) / MILLIS_IN.ONE_YEAR);
    return formatRelativeTime(years, `${abbreviate ? "yr" : "year"}`, diffInMillis > 0, abbreviate);
  }
}

///////////////////
// Absolute Time //
///////////////////

function formatAbsoluteTime(startTime: string, endTime?: string) {
  if (!endTime) {
    return startTime;
  }

  return `${startTime} – ${endTime}`;
}

export function getAbsoluteTime(startTime: ZonedMoment, endTime?: ZonedMoment) {
  const start = startTime.format(`h:mma`);
  const end = endTime ? endTime.format(`h:mma`) : undefined;

  return formatAbsoluteTime(start, end);
}

export function getAbsoluteDate(
  startTime: ZonedMoment,
  endTime?: ZonedMoment,
  showDay?: boolean,
  forceShowYear?: boolean,
) {
  const now = new ZonedMoment();
  const showYear = forceShowYear || !startTime.isSame(endTime || now, "y");

  const formattedStart = startTime.format(
    `${showDay ? "ddd" : ""} MMM D${showYear ? ", YYYY" : ""}`,
  );

  if (!endTime || startTime.isSame(endTime, "d")) {
    return formatAbsoluteTime(formattedStart);
  }

  const formattedEnd = endTime.format(`${showDay ? "ddd" : ""} MMM D${showYear ? ", YYYY" : ""}`);
  return formatAbsoluteTime(formattedStart, formattedEnd);
}

export function getAbsoluteDateTime(startTime: ZonedMoment, endTime?: ZonedMoment) {
  const now = new ZonedMoment();
  const showYear = !startTime.isSame(endTime || now, "y");

  if (!endTime) {
    const startWithoutEnd = startTime.format(`ddd MMM D [at] h:mma${showYear ? ", YYYY" : ""}`);
    return formatAbsoluteTime(startWithoutEnd);
  }

  const isSameDate = startTime.isSame(endTime, "d");
  const start = startTime.format(`ddd MMM D, h:mma${showYear ? ", YYYY" : ""}`);
  const end = endTime.format(`${!isSameDate ? "ddd MMM D, " : ""}h:mma${showYear ? ", YYYY" : ""}`);

  return formatAbsoluteTime(start, end);
}

export function getAbsoluteDateTimeForMinMaxString(minDate: string, maxDate: string) {
  if (!minDate || minDate.split("-").length !== 3 || !maxDate || maxDate.split("-").length !== 3) {
    console.warn(
      "getAbsoluteDateTimeForMinMaxString passed invalid strings. Min and Max dates must be provided in YYYY-MM-DD formats",
    );
    return "";
  }
  const startDate = new ZonedMoment(minDate, "UTC", "YYYY-MM-DD");
  const endDate = new ZonedMoment(maxDate, "UTC", "YYYY-MM-DD");
  return getAbsoluteDate(startDate, endDate, true);
}

export function getAbsoluteDateTimeForMoveHistory(eventMoved: DefragExplainedMove) {
  if (!validateEventMoved(eventMoved)) {
    return { originalTime: "", newTime: "" };
  }

  const meetingDuration = eventMoved.event.endTime.millis - eventMoved.event.startTime.millis;

  const originalStartMillis = eventMoved.originalStartTime.millis;
  const originalZonedStart = new ZonedMoment(originalStartMillis);
  const originalZonedEnd = new ZonedMoment(originalStartMillis + meetingDuration);
  const originalTime = getAbsoluteDateTime(originalZonedStart, originalZonedEnd);

  const newStartMillis = eventMoved.newStartTime.millis;
  const newZonedStart = new ZonedMoment(newStartMillis);
  const newZonedEnd = new ZonedMoment(newStartMillis + meetingDuration);
  const newTime = getAbsoluteDateTime(newZonedStart, newZonedEnd);

  return {
    originalTime,
    newTime,
  };
}

/////////////////////////////
// ABSOLUTE TIME FOR EVENT
/////////////////////////////
export function getAbsoluteDateTimeForEvent(
  event: SetRequired<Partial<Event>, "startTime" | "endTime">,
) {
  const startTime = event && event.startTime;
  const endTime = event && event.endTime;

  if (!validateEventTime(startTime) || !validateEventTime(endTime)) {
    return;
  }

  if (startTime.type === "Date") {
    // special handling for dates
    const startDate = new ZonedMoment(startTime.dateOrDateTime, "UTC");
    const endDate = new ZonedMoment(endTime.dateOrDateTime, "UTC").subtract(1, "days"); // end is exclusive
    return getAbsoluteDate(startDate, endDate);
  }

  return getAbsoluteDateTime(new ZonedMoment(startTime.millis), new ZonedMoment(endTime.millis));
}

export function getAbsoluteTimeForEvent(
  event: SetRequired<Partial<Event>, "startTime" | "endTime">,
) {
  const startTime = event && event.startTime;
  const endTime = event && event.endTime;

  if (!validateEventTime(startTime) || !validateEventTime(endTime)) {
    return;
  }

  if (startTime.type === "Date") {
    // time doesn't make sense for dates
    return;
  }

  return getAbsoluteTime(new ZonedMoment(startTime.millis), new ZonedMoment(endTime.millis));
}

export function getAbsoluteDateForEvent(
  event?: SetRequired<Partial<Event>, "startTime" | "endTime">,
  showDay?: boolean,
) {
  const startTime = event && event.startTime;
  const endTime = event && event.endTime;

  if (!startTime || !endTime || !validateEventTime(startTime) || !validateEventTime(endTime)) {
    return;
  }

  if (startTime.type === "Date") {
    // special handling for dates
    const startDate = new ZonedMoment(startTime.dateOrDateTime, "UTC");
    const endDate = new ZonedMoment(endTime.dateOrDateTime, "UTC").subtract(1, "days"); // end is exclusive
    return getAbsoluteDate(startDate, endDate, showDay);
  }

  return getAbsoluteDate(
    new ZonedMoment(startTime.millis),
    new ZonedMoment(endTime.millis),
    showDay,
  );
}

/////////////////////
// Calendar Status //
/////////////////////

export function getEmojiAndDescriptionForCalendarStatus(calendarStatus: CalendarStatus | null) {
  let emoji = "";
  let description = "";
  if (!calendarStatus) {
    return { emoji, description };
  }

  switch (calendarStatus.status) {
    case "FocusTime":
      emoji = "💡";
      description = "Focus Time";
      break;
    case "Meeting":
      emoji = "🗓️";
      description = "In a meeting";
      break;
    case "OutOfOffice":
      emoji = "⛔";
      description = "Out of office";
      break;
    case "Weekend":
      emoji = "🖼️";
      description = "Weekend";
      break;
    case "DayOff":
      emoji = "🖼️";
      description = "Day off";
      break;
    case "WorkingHours":
      emoji = "💻";
      description = "In working hours";
      break;
    case "Lunch":
      emoji = "🍽";
      description = "Lunch";
      break;
    case "BeforeWorkingHours":
      emoji = getMoonPhaseEmoji(calendarStatus.moonPhase);
      description = "Before working hours";
      break;
    case "AfterHours":
      emoji = getMoonPhaseEmoji(calendarStatus.moonPhase);
      description = "After hours";
      break;
    case "Holiday":
      emoji = "⛔";
      description = "Holiday";
      break;
    default:
      break;
  }
  return { emoji, description };
}

function getMoonPhaseEmoji(moonPhase?: Maybe<MoonPhase>) {
  switch (moonPhase) {
    case "NewMoon":
      return "🌑";
    case "WaxingCrescentMoon":
      return "🌒";
    case "FirstQuarterMoon":
      return "🌓";
    case "WaxingGibbousMoon":
      return "🌔";
    case "FullMoon":
      return "🌕";
    case "WaningGibbousMoon":
      return "🌖";
    case "LastQuarterMoon":
      return "🌗";
    case "WaningCrescentMoon":
      return "🌘";
    default:
      return "";
  }
}
